import React, { useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';
import dayjs from 'dayjs';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { usePriceDrawerStore } from '@common/store/priceDrawer';
import { useTailwindColor } from '@common/hooks/useTailwindColors';
import { Typography } from '@common/components/foundations/Typography';
import { Card } from '@common/components/molecules/Card';
import { formattedCurrency } from '@pages/Client/Dashboard/utils/formattedCurrency';
import { formattedPercentage } from '@pages/Client/Dashboard/utils/formattedPercentage';
import { API_DATE_FORMAT } from '@common/constants/date';
import { ScrollArea, ScrollBar } from '@common/components/molecules/ScrollArea';
import { useDate } from '@common/hooks/useDate';

type DataKey = 'tly' | 'stly' | 'total';

interface Props {
  tly: { date: string; value: number }[];
  stly: { date: string; value: number }[];
  total: { date: string; value: number }[];
  budget?: { date: string; value: number }[];
  forecast?: { date: string; value: number }[];
  isCurrency?: boolean;
}

interface CustomBarProps {
  fill: string;
  x: number;
  y: number;
  width: number;
  height: number;
  budget?: number;
  maxY: number;
  dataKey: DataKey;
  seriesVisibility: {
    [key: string]: boolean;
  };
  borderWidth?: number;
  borderRadius?: number;
}

export const DailyKPIChart: React.FC<Props> = ({ tly, stly, total, isCurrency }) => {
  const { t } = useTranslation();
  const { setDrawerState, setViewingDate } = usePriceDrawerStore();
  const indigo = useTailwindColor('indigo');
  const gold = useTailwindColor('gold');
  const orange = useTailwindColor('orange');
  const grey = useTailwindColor('grey');
  const mediumGrey = useTailwindColor('mediumGrey');
  const darkGrey = useTailwindColor('darkGrey');
  const fontFamily = "'Inter var', sans-serif";
  const fontSize = '12px';
  const fontWeight = 400;

  const [seriesVisibility, setSeriesVisibility] = useState<Record<DataKey, boolean>>({
    tly: true,
    stly: true,
    total: true
  });

  const data = useMemo(() => {
    const mergedData = total.map((item) => {
      const currentDate = dayjs(item.date);
      const previousYearDate = currentDate.subtract(1, 'year').format('YYYY-MM-DD');
      return {
        date: item.date,
        total: item.value,
        tly: tly.find((x) => x.date === previousYearDate)?.value ?? 0,
        stly: stly.find((x) => x.date === previousYearDate)?.value ?? 0
      };
    });
    return mergedData;
  }, [tly, stly, total]);

  const maxY = useMemo(() => {
    const allValues = [
      ...tly.map((item) => item.value),
      ...stly.map((item) => item.value),
      ...total.map((item) => item.value)
    ];

    return Math.max(...allValues);
  }, [tly, stly, total]);

  const CustomBar: React.FC<CustomBarProps> = ({
    fill,
    x,
    y,
    width,
    height,
    dataKey,
    seriesVisibility,
    borderWidth = 1,
    borderRadius = 4
  }) => {
    const barVisible = seriesVisibility[dataKey];

    const adjustedHeight = Math.max(0, height);
    const adjustedY = height < 0 ? y + height : y;

    return (
      <g>
        {barVisible ? (
          <>
            {adjustedHeight > 0 ? (
              <rect
                x={x}
                y={adjustedY}
                width={width}
                height={adjustedHeight}
                fill={fill}
                rx={borderRadius}
                ry={borderRadius}
              />
            ) : null}
            {adjustedHeight > borderWidth * 2 ? (
              <rect
                x={x + borderWidth}
                y={adjustedY + borderWidth}
                width={width - borderWidth * 2}
                height={adjustedHeight - borderWidth * 2}
                fill={fill}
                rx={borderRadius - borderWidth}
                ry={borderRadius - borderWidth}
              />
            ) : null}
          </>
        ) : null}
      </g>
    );
  };

  const CustomLegend: React.FC<any> = (props) => {
    const { payload } = props;

    // Add onClick handler to toggle series visibility
    const handleLegendClick = (dataKey: DataKey) => {
      setSeriesVisibility((prev) => ({ ...prev, [dataKey]: !prev[dataKey] }));
    };

    return (
      <ul className="mb-8 flex list-none justify-center">
        {payload?.map(
          (entry: { dataKey: DataKey; value: string; color: string }, index: number) => (
            <li
              key={`item-${index}`}
              className="mr-2 flex cursor-pointer items-center"
              onClick={() => handleLegendClick(entry.dataKey)}>
              <div
                className="mr-1 h-3 w-3 rounded-full"
                style={{
                  backgroundColor: entry.color,
                  opacity: seriesVisibility[entry.dataKey] ? 1 : 0.3
                }}
              />
              <Typography
                color="copyTextGrey"
                variant="meta-2"
                className={clsx(seriesVisibility[entry.dataKey] ? 'opacity-100' : 'opacity-30')}>
                {entry.value}
              </Typography>
            </li>
          )
        )}
      </ul>
    );
  };

  const CustomTooltip: React.FC<any> = (props) => {
    const { active, payload } = props;
    if (!active || !payload || !payload[0]) return null;
    const dataPoint = payload[0].payload;
    return (
      <Card backgroundColor="white">
        <Typography variant="meta-1" className="font-semibold" color="darkGrey">
          {dayjs(dataPoint.date).format('ddd, DD MMM YYYY')}
        </Typography>
        <div className="flex items-center justify-between gap-x-3">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-orange" />
            <Typography variant="meta-2" color="darkGrey">
              {t('Total Last Year')}
            </Typography>
          </div>
          <Typography variant="meta-2" className="text-meta-2-medium" color="darkGrey">
            <b>
              {isCurrency ? formattedCurrency(dataPoint.tly) : formattedPercentage(dataPoint.tly)}
            </b>
          </Typography>
        </div>
        <div className="flex items-center justify-between gap-x-3">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-gold" />
            <Typography variant="meta-2" color="darkGrey">
              {t('Same Time Last Year')}
            </Typography>
          </div>
          <Typography variant="meta-2" className="text-meta-2-medium" color="darkGrey">
            <b>
              {isCurrency ? formattedCurrency(dataPoint.stly) : formattedPercentage(dataPoint.stly)}
            </b>
          </Typography>
        </div>
        <div className="flex items-center justify-between gap-x-3">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-indigo" />
            <Typography variant="meta-2" color="darkGrey">
              {t('On the Books')}
            </Typography>
          </div>
          <Typography variant="meta-2" className="text-meta-2-medium" color="darkGrey">
            <b>
              {isCurrency
                ? formattedCurrency(dataPoint.total)
                : formattedPercentage(dataPoint.total)}
            </b>
          </Typography>
        </div>
        <div className="mt-2 text-xs italic text-copyTextGrey">
          {t('Click the chart for more details.')}
        </div>
      </Card>
    );
  };

  const customLegendPayload = [
    {
      dataKey: 'tly',
      value: t('Total Last Year'),
      color: orange
    },
    {
      dataKey: 'stly',
      value: t('Same Time Last Year'),
      color: gold
    },
    {
      dataKey: 'total',
      value: t('On the Books'),
      color: indigo
    }
  ];

  const CustomTick: React.FC<any> = ({ x, y, payload }) => {
    const { isToday, isWeekend } = useDate();
    const date = new Date(payload.value);

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={16}
          dy={0}
          textAnchor="end"
          fill={isToday(date) ? darkGrey : grey}
          fontFamily={fontFamily}
          fontSize={fontSize}
          fontWeight={isToday(date) ? 'bold' : fontWeight}
          textDecoration={isWeekend(date) ? 'underline' : 'none'}
          transform="rotate(-45 0 16)">
          {dayjs(payload.value).format('MMM DD')}
        </text>
      </g>
    );
  };

  const handleOpenPriceDrawer = (date: string | undefined) => {
    if (!date) return;
    const formattedDate = new Date(dayjs(date).format(API_DATE_FORMAT));
    setDrawerState(true);
    setViewingDate(formattedDate);
  };

  return (
    <Card backgroundColor="white">
      <ScrollArea>
        <ResponsiveContainer minWidth={1000} width="100%" height={600}>
          <ComposedChart
            onClick={(data: any) => {
              handleOpenPriceDrawer(data?.activeLabel);
            }}
            data={data}
            margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
            barGap={-32}
            barCategoryGap={10}>
            <CartesianGrid stroke={mediumGrey} strokeDasharray="2" vertical={false} />
            <XAxis
              axisLine={false}
              height={80}
              tickLine={false}
              fontFamily={fontFamily}
              fontSize={fontSize}
              fontWeight={fontWeight}
              dataKey="date"
              tick={<CustomTick />}
              interval={0} // Show all labels
            />
            <YAxis
              domain={!isCurrency ? [0, 100] : undefined}
              axisLine={false}
              tick={{ fill: grey }}
              tickLine={false}
              width={80}
              fontFamily={fontFamily}
              fontSize={fontSize}
              fontWeight={fontWeight}
              tickFormatter={(value) =>
                isCurrency ? formattedCurrency(value) : formattedPercentage(value)
              }
            />
            <Tooltip
              content={(props) => <CustomTooltip {...props} />}
              wrapperStyle={{ outline: 'none' }}
            />
            <Legend
              verticalAlign="top"
              content={(props) => <CustomLegend {...props} payload={customLegendPayload} />}
            />

            <Bar
              visibility={seriesVisibility.total ? 'visible' : 'hidden'}
              dataKey="total"
              name="On the Books"
              fill={indigo}
              barSize={32}
              shape={(props) => (
                <CustomBar
                  {...props}
                  maxY={maxY}
                  seriesVisibility={seriesVisibility}
                  dataKey="total"
                />
              )}
            />

            <Line
              isAnimationActive={false}
              visibility={seriesVisibility.stly ? 'visible' : 'hidden'}
              dataKey="stly"
              name="Same Time Last Year"
              stroke={gold}
              fill={gold}
            />

            <Line
              isAnimationActive={false}
              visibility={seriesVisibility.tly ? 'visible' : 'hidden'}
              dataKey="tly"
              name="Total Last Year"
              stroke={orange}
              fill={orange}
            />
          </ComposedChart>
        </ResponsiveContainer>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </Card>
  );
};
